import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { CsvButton } from '@/components/CsvButton'
import { DaysSelect } from '@/components/DaysSelect'
import { RefreshButton } from '@/components/RefreshButton'
import { downloadCsv } from '@/lib/csv'

// A16.33 — Kanal boostlari (qo'llab-quvvatlash). Modul-lokal, alohida fayl (band-fayllar
// AnalyticsMetrics/Screens boshqa sessiyalar tomonidan tahrirlanayotgani uchun izolyatsiya).
// Backend: GET /admin/analytics/channel-boosts (`handlers/admin.go` AnalyticsChannelBoosts). PII-siz.
interface ChannelBoostsData {
  total_boosts: number
  boosted_channels: number
  boosters: number
  boosts_7d: number
  top_channels: { title: string; boosts: number }[]
  per_day: { day: string; boosts: number }[]
}
function fetchChannelBoosts(days: number, signal?: AbortSignal): Promise<ChannelBoostsData> {
  return api.get<ChannelBoostsData>(`/admin/analytics/channel-boosts?days=${days}`, signal ? { signal } : undefined)
}

function KPI({ label, value, loading, accent }: { label: string; value?: number; loading: boolean; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** A16.33 — Kanal boostlari (qo'llab-quvvatlash statistikasi + top kanallar + trend). */
export function ChannelBoosts() {
  const t = useT()
  const [data, setData] = useState<ChannelBoostsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [days, setDays] = useState(14)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchChannelBoosts(days, ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload, days])
  const top = data?.top_channels ?? []
  const trendBars = useMemo<BarDatum[]>(
    () => (data?.per_day ?? []).map((d) => ({ label: d.day.slice(5), value: d.boosts, title: d.day })),
    [data],
  )
  const handleExport = () => {
    if (!data) return
    downloadCsv('channel-boosts', [
      [t.analytics.cbTotal, t.analytics.cbChannels, t.analytics.cbBoosters, t.analytics.cb7d],
      [data.total_boosts, data.boosted_channels, data.boosters, data.boosts_7d],
      [],
      [t.analytics.cbTop, t.analytics.cbTotal],
      ...top.map((c) => [c.title, c.boosts]),
    ])
  }
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.channelBoosts}
        screenId="A16.33"
        description={t.analytics.channelBoostsNote}
        actions={
          <div className="flex items-center gap-2">
            <DaysSelect value={days} onChange={setDays} />
            <RefreshButton onClick={() => setReload((n) => n + 1)} />
            {data && <CsvButton onClick={handleExport} />}
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.cbTotal} value={data?.total_boosts} loading={loading} accent />
          <KPI label={t.analytics.cbChannels} value={data?.boosted_channels} loading={loading} />
          <KPI label={t.analytics.cbBoosters} value={data?.boosters} loading={loading} />
          <KPI label={t.analytics.cb7d} value={data?.boosts_7d} loading={loading} />
        </div>
        <Panel title={t.analytics.cbTop}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : !top.length ? (
            <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
          ) : (
            <ol className="space-y-1">
              {top.map((c, i) => (
                <li
                  key={`${c.title}-${i}`}
                  className="flex items-center gap-3 rounded-md border border-border-subtle bg-surface-1 px-3 py-2 text-sm"
                >
                  <span className="w-6 shrink-0 text-right font-mono text-xs text-tertiary">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-primary">{c.title}</span>
                  <span className="shrink-0 font-mono tabular text-primary">{c.boosts.toLocaleString()}</span>
                </li>
              ))}
            </ol>
          )}
        </Panel>
        <Panel title={`${t.analytics.cbTrend} · ${t.analytics.daysLabel(days)}`}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={trendBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}
